import { type ComponentType, type ReactNode } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import SeoScoreCard from '@/app/ContentTools/components/ai-content-analysis/SeoScoreCard';
import { AiAnalysisReportSkeleton } from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSkeletons';
import AiAnalysisPdfDownloadMenu from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisPdfDownloadMenu';
import { mapContentOverviewSeo } from '@/app/ContentTools/utils/mapContentOverview';
import type { ViewHistoryMessageData } from '@/app/ContentTools/types/aiContentAnalysisApiTypes';

type FalconCardHeaderProps = {
  className?: string;
  light?: boolean;
  title: ReactNode;
  endEl?: ReactNode;
};

type SeoAnalysisDetailsPanelProps = {
  data?: ViewHistoryMessageData;
  analysisId: string;
  isLoading: boolean;
  markdownReport: ComponentType<{ message: string }>;
};

const TypedFalconCardHeader =
  FalconCardHeader as ComponentType<FalconCardHeaderProps>;

const isSeoMissing = (item?: ViewHistoryMessageData) =>
  Boolean(item) && item?.seo_overview == null && item?.rank == null;

const SeoAnalysisDetailsPanel = ({
  data,
  analysisId,
  isLoading,
  markdownReport: MarkdownReport
}: SeoAnalysisDetailsPanelProps) => {
  const seoDetailsData = data?.seo_overview
    ? mapContentOverviewSeo({
        keyword: data.keyword || '',
        seo_overview: data.seo_overview
      })
    : null;

  if (isLoading) {
    return <AiAnalysisReportSkeleton />;
  }

  if (isSeoMissing(data)) {
    const existingKeyword = data?.keyword?.trim();
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <p className="fw-semi-bold mb-0">
            {existingKeyword
              ? `برای کلمه «${existingKeyword}» تحلیل سئویی یافت نشد.`
              : 'برای این مورد تحلیل سئویی یافت نشد.'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col xs={12} lg={8} className="order-1 order-lg-0">
        <Card className="w-100">
          <TypedFalconCardHeader
            className="fs--1"
            light
            title={
              <span className="text-muted fw-bold fs-0">
                {data?.keyword || ''}
              </span>
            }
            endEl={
              <AiAnalysisPdfDownloadMenu
                id={data?.id || analysisId}
                currentType="seo_analysis"
                keyword={data?.keyword}
              />
            }
          />
          <Card.Body>
            <MarkdownReport message={data?.message || ''} />
          </Card.Body>
        </Card>
      </Col>
      <Col xs={12} lg={4} className="order-0 order-lg-1">
        <div
          className="sticky-top"
          style={{
            top: '1rem',
            maxHeight: 'calc(100vh - 2rem)',
            overflowY: 'auto'
          }}
        >
          {seoDetailsData ? <SeoScoreCard data={seoDetailsData} /> : null}
        </div>
      </Col>
    </Row>
  );
};

export default SeoAnalysisDetailsPanel;
